// NumberInput.tsx - ИСПРАВЛЕННАЯ ВЕРСИЯ С ПОДДЕРЖКОЙ ДРОБНЫХ ЧИСЕЛ
import React, { useState, useCallback } from 'react';
import { TextInput, FormControl } from '@primer/react';
import { XCircleFillIcon } from '@primer/octicons-react';

interface NumberInputProps {
    value: string;
    onChange: (value: string) => void;
    type?: 'from' | 'to';
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    error?: boolean;
    errorMessage?: string;
    allowNegative?: boolean;
    allowDecimal?: boolean;
}

const NumberInput: React.FC<NumberInputProps> = ({
    value,
    onChange,
    type = 'from',
    placeholder,
    className = '',
    disabled = false,
    error = false,
    errorMessage,
    allowNegative = false,
    allowDecimal = true
}) => {
    const [isFocused, setIsFocused] = useState(false);

    const formatNumber = (num: string): string => {
        if (!num || num === '-') return num;

        const isNegative = num.startsWith('-');
        const absoluteValue = isNegative ? num.slice(1) : num;

        // Разделяем на целую и дробную части
        const parts = absoluteValue.split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1];

        // Форматируем целую часть с разделителями тысяч
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

        // Собираем обратно
        let result = formattedInteger;
        if (decimalPart !== undefined) {
            result += ',' + decimalPart;
        } else if (absoluteValue.endsWith('.')) {
            result += ',';
        }

        return isNegative ? `-${result}` : result;
    };

    const getDisplayValue = (): string => {
        if (!value) return '';
        const formatted = formatNumber(value);
        if (!formatted || formatted === '-') return '';
        const prefix = type === 'from' ? 'От ' : 'До ';
        return `${prefix}${formatted}`;
    };

    const getRawValue = (): string => {
        // В фокусе показываем с точкой как разделитель десятичных
        return value.replace('.', ',');
    };

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        let inputValue = e.target.value;

        // Заменяем запятую на точку для внутреннего хранения
        inputValue = inputValue.replace(',', '.');

        const hasMinusAtStart = inputValue.startsWith('-');

        if (allowDecimal) {
            // Разрешаем цифры, точку и минус
            let cleaned = inputValue.replace(/[^\d.-]/g, '');

            // Убираем все минусы кроме первого
            if (hasMinusAtStart) {
                cleaned = '-' + cleaned.slice(1).replace(/-/g, '');
            } else {
                cleaned = cleaned.replace(/-/g, '');
            }

            // Разрешаем только одну точку
            const parts = cleaned.split('.');
            if (parts.length > 2) {
                cleaned = parts[0] + '.' + parts.slice(1).join('');
            }

            // Если только минус, точка или минус с точкой
            if (cleaned === '-' || cleaned === '.' || cleaned === '-.') {
                if (!allowNegative && cleaned.includes('-')) {
                    onChange('');
                } else {
                    onChange(cleaned);
                }
                return;
            }

            // Убираем ведущие нули (кроме "0.")
            if (cleaned.length > 1 && cleaned[0] === '0' && cleaned[1] !== '.') {
                cleaned = cleaned.replace(/^0+/, '');
            }
            if (hasMinusAtStart && cleaned.length > 2 && cleaned[1] === '0' && cleaned[2] !== '.') {
                cleaned = '-' + cleaned.slice(1).replace(/^0+/, '');
            }

            if (!allowNegative && hasMinusAtStart) {
                onChange(cleaned.slice(1));
            } else {
                onChange(cleaned);
            }
        } else {
            // Только целые числа
            const numericPart = inputValue.replace(/[^\d]/g, '');

            if (numericPart === '') {
                onChange(hasMinusAtStart && allowNegative ? '-' : '');
                return;
            }

            const normalized = String(parseInt(numericPart, 10));

            if (!allowNegative) {
                onChange(normalized);
            } else {
                onChange(hasMinusAtStart ? `-${normalized}` : normalized);
            }
        }
    }, [onChange, allowNegative, allowDecimal]);

    const handleClear = useCallback(() => {
        onChange('');
    }, [onChange]);

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        // Убираем некорректные значения при потере фокуса
        if (value === '-' || value === '.' || value === '-.') {
            onChange('');
        }
        // Убираем точку в конце
        if (value.endsWith('.')) {
            onChange(value.slice(0, -1));
        }
        setIsFocused(false);
    };

    const getPlaceholder = (): string => {
        if (placeholder) return placeholder;
        return type === 'from' ? 'От' : 'До';
    };

    return (
        <FormControl required key={type}>
            <FormControl.Label className='invisible h-0'></FormControl.Label>
            <TextInput
                trailingAction={
                    value.length > 0 && value !== '-' && value !== '.' && value !== '-.' ? (
                        <TextInput.Action
                            onClick={handleClear}
                            icon={XCircleFillIcon}
                            aria-label="Очистить"
                        />
                    ) : undefined
                }
                type="text"
                value={isFocused ? getRawValue() : getDisplayValue()}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={getPlaceholder()}
                disabled={disabled}
                contrast={!!(value && value.trim() && value !== '-' && value !== '.' && value !== '-.')}
            />
            {error && errorMessage && (
                <FormControl.Validation variant="error" className='invisible h-0'></FormControl.Validation>
            )}
        </FormControl>
    );
};

export default NumberInput;